import { useCallback, useEffect, useState } from 'react';

export interface SellerBoostItem {
  id: string;
  itemId: string;
  title: string;
  sku?: string | null;
  price?: number | null;
  thumbnailUrl?: string | null;
  isBoosted: boolean;
  coolDownSecond?: number | null;
}

export interface SellerAutoBoostStatus {
  enabled: boolean;
  itemIds: string[];
  connectionId: string;
  currentIndex: number;
  lastBoostAt: string | null;
  nextBoostAt: string | null;
  totalItems: number;
  currentBatchStart: number;
  lastResult?: any;
}

export interface SellerBoostStatus {
  items: SellerBoostItem[];
  boostedCount: number;
  availableSlots: number;
  totalSlots: number;
  autoBoost: SellerAutoBoostStatus | null;
}

function headers() {
  const token = localStorage.getItem('auth_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function readJson(res: Response) {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.message || json.error || `HTTP ${res.status}`);
  return json;
}

// `poll`: recarrega a cada 30 s (cada carga consulta a Shopee). Desligado, carrega uma vez.
export function useSellerBoost(connectionId?: string, { poll = true }: { poll?: boolean } = {}) {
  const [data, setData] = useState<SellerBoostStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!connectionId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/seller/boost/shopee?connectionId=${encodeURIComponent(connectionId)}`, { headers: headers() });
      setData(await readJson(res));
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar impulsionamento');
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    refetch();
    if (!poll) return;
    const timer = window.setInterval(refetch, 30000);
    return () => window.clearInterval(timer);
  }, [refetch, poll]);

  const boostItems = useCallback(async (itemIds: string[]) => {
    if (!connectionId) throw new Error('Selecione uma conta Shopee');
    setSaving(true);
    try {
      const res = await fetch('/api/seller/boost/shopee', { method: 'POST', headers: headers(), body: JSON.stringify({ connectionId, itemIds }) });
      const json = await readJson(res);
      await refetch();
      return json;
    } finally {
      setSaving(false);
    }
  }, [connectionId, refetch]);

  const saveAutoBoost = useCallback(async (itemIds: string[], enabled: boolean) => {
    if (!connectionId) throw new Error('Selecione uma conta Shopee');
    setSaving(true);
    try {
      const res = await fetch('/api/seller/boost/shopee/auto', { method: 'POST', headers: headers(), body: JSON.stringify({ connectionId, itemIds, enabled }) });
      const json = await readJson(res);
      await refetch();
      return json;
    } finally {
      setSaving(false);
    }
  }, [connectionId, refetch]);

  const runAutoBoostNow = useCallback(async () => {
    if (!connectionId) throw new Error('Selecione uma conta Shopee');
    setSaving(true);
    try {
      const res = await fetch('/api/seller/boost/shopee/auto/run', { method: 'POST', headers: headers(), body: JSON.stringify({ connectionId }) });
      const json = await readJson(res);
      await refetch();
      return json;
    } finally {
      setSaving(false);
    }
  }, [connectionId, refetch]);

  return { data, loading, saving, error, refetch, boostItems, saveAutoBoost, runAutoBoostNow };
}
